(function initVideoStory() {
  function parseCount(value) {
    const match = String(value || "").match(/^(.*?)(\d[\d,]*)(.*)$/);
    if (!match) return null;
    return {
      prefix: match[1],
      number: Number.parseInt(match[2].replace(/,/g, ""), 10),
      suffix: match[3],
    };
  }

  function formatCount(prefix, number, suffix) {
    return prefix + number.toLocaleString("en-US") + suffix;
  }

  function prepareVideos(section) {
    const templateVideo = section.querySelector("template")?.content?.querySelector("video");
    const videos = [...section.querySelectorAll("video")];
    if (templateVideo) videos.push(templateVideo);

    videos.forEach((video) => {
      try {
        video.muted = true;
        video.setAttribute("playsinline", "");
        video.playsInline = true;
      } catch (_error) {
        // Ignore media attribute errors from restricted environments.
      }
    });
  }

  function animateCount(item) {
    if (!item?.el) return;
    const duration = item.number === 0 ? 900 : 1800;
    const start = performance.now();
    const from = item.number === 0 ? 18 : 0;
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(from + (item.number - from) * eased);
      item.el.textContent = formatCount(item.prefix, current, item.suffix);
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  function setupFlowItems(section) {
    const items = [];

    const media = section.querySelector(".video-story__media");
    if (media) items.push(media);

    const kicker = section.querySelector(".video-story__kicker");
    if (kicker) items.push(kicker);

    const heading = section.querySelector(".video-story__heading");
    if (heading) items.push(heading);

    const copy = section.querySelector(".video-story__copy");
    const paragraphs = copy ? [...copy.children].filter((node) => node.tagName === "P") : [];
    if (paragraphs.length > 0) {
      paragraphs.forEach((paragraph) => items.push(paragraph));
    } else if (copy) {
      items.push(copy);
    }

    section.querySelectorAll(".video-story__stat").forEach((stat) => items.push(stat));

    items.forEach((element, index) => {
      element.classList.add("video-story__flow-item");
      element.style.setProperty("--flow-index", String(index));
    });
  }

  function startPlayer(player, { unmute = false, showControls = false } = {}) {
    if (!player || player.classList.contains("is-playing")) return;

    const iframe = player.querySelector("iframe.js-youtube, iframe.js-vimeo, iframe[data-src]");
    if (iframe) {
      const raw = iframe.getAttribute("data-src") || iframe.getAttribute("src") || "";
      if (raw) {
        let nextSrc = raw;
        try {
          const url = new URL(raw, window.location.origin);
          url.searchParams.set("autoplay", "1");
          url.searchParams.set("playsinline", "1");
          if (iframe.classList.contains("js-youtube")) {
            url.searchParams.set("enablejsapi", "1");
            url.searchParams.set("rel", "0");
            url.searchParams.set("modestbranding", "1");
            // Browsers require mute for programmatic autoplay; unmute after start when requested.
            url.searchParams.set("mute", unmute ? "0" : "1");
            if (showControls) {
              url.searchParams.set("controls", "1");
              url.searchParams.set("fs", "1");
            }
          } else if (iframe.classList.contains("js-vimeo")) {
            url.searchParams.set("muted", unmute ? "0" : "1");
            if (showControls) {
              url.searchParams.set("controls", "1");
              url.searchParams.set("background", "0");
            }
          }
          nextSrc = url.toString();
        } catch (_error) {
          nextSrc = raw.includes("autoplay=")
            ? raw
            : raw + (raw.includes("?") ? "&" : "?") + "autoplay=1";
        }
        iframe.setAttribute("src", nextSrc);
        iframe.removeAttribute("data-src");
        iframe.style.pointerEvents = "auto";
      }
    }

    const videoLocal = player.closest("video-local") || player;
    const deferredVideo = videoLocal.querySelector("template");
    if (deferredVideo && !player.querySelector("video")) {
      if (typeof videoLocal.loadContent === "function") {
        videoLocal.loadContent();
      } else {
        const content = deferredVideo.content.cloneNode(true);
        player.appendChild(content);
      }
    }

    const inlineVideo = player.querySelector("video");
    if (inlineVideo) {
      try {
        inlineVideo.muted = !unmute;
        inlineVideo.playsInline = true;
        inlineVideo.setAttribute("playsinline", "");
        if (showControls) inlineVideo.controls = true;
        const playPromise = inlineVideo.play?.();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      } catch (_error) {
        // Ignore play failures (autoplay policies, etc.).
      }
    }

    player.classList.add("is-playing");
    if (showControls) player.classList.add("video-story__player--controls");
    player.querySelectorAll(".video-story__play").forEach((button) => {
      button.hidden = true;
      button.setAttribute("aria-hidden", "true");
    });
    player.querySelectorAll(".video-story__poster").forEach((poster) => {
      poster.hidden = true;
    });
  }

  function setupPlayButtons(section) {
    section.querySelectorAll(".video-story__play").forEach((button) => {
      if (button.tagName !== "BUTTON") return;
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const player = button.closest(".video-story__player");
        startPlayer(player, { unmute: true, showControls: true });
      });
    });
  }

  function setupAutoplay(section) {
    if (section.dataset.autoplay !== "true") return;

    const players = [...section.querySelectorAll(".video-story__player--autoplay")];
    if (players.length === 0) return;

    let started = false;
    const playAll = () => {
      if (started) return;
      started = true;
      players.forEach((player) => {
        startPlayer(player, { unmute: false, showControls: false });
        player.classList.add("is-playing");
      });
    };

    if (!("IntersectionObserver" in window)) {
      playAll();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          playAll();
          observer.disconnect();
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
    );
    observer.observe(section);
    section._videoAutoplayObserver = observer;

    requestAnimationFrame(() => {
      if (isInView(section)) {
        playAll();
        observer.disconnect();
      }
    });
  }

  function isInView(section) {
    const rect = section.getBoundingClientRect();
    const view = window.innerHeight || 1;
    return rect.top < view * 0.9 && rect.bottom > view * 0.1;
  }

  function setup(root) {
    if (!root || typeof root.querySelectorAll !== "function") return;

    const sections = root.matches?.("[data-video-story]")
      ? [root]
      : [...root.querySelectorAll("[data-video-story]")];

    sections.forEach((section) => {
      if (section.dataset.ready === "true") return;
      section.dataset.ready = "true";

      try {
        prepareVideos(section);
        setupFlowItems(section);
        setupPlayButtons(section);
        setupAutoplay(section);

        const animateEnabled = section.dataset.animate !== "false";
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const stats = section.querySelector("[data-story-stats]");
        const counters = [];
        const timers = [];

        section.querySelectorAll("[data-count-value]").forEach((wrap) => {
          const parsed = parseCount(wrap.dataset.countValue);
          const run = wrap.querySelector(".video-story__stat-run");
          if (!parsed || !run) return;
          counters.push({
            el: run,
            prefix: parsed.prefix,
            suffix: parsed.suffix,
            number: parsed.number,
          });
          run.textContent = formatCount(parsed.prefix, parsed.number === 0 ? 18 : 0, parsed.suffix);
        });

        const setFinalCounts = () => {
          counters.forEach((item) => {
            item.el.textContent = formatCount(item.prefix, item.number, item.suffix);
          });
        };

        const observers = [];
        let onScroll = null;
        let revealed = false;
        let safetyTimer = null;

        const cleanup = () => {
          observers.forEach((observer) => observer.disconnect());
          observers.length = 0;
          timers.forEach((id) => window.clearTimeout(id));
          timers.length = 0;
          if (safetyTimer) {
            window.clearTimeout(safetyTimer);
            safetyTimer = null;
          }
          if (section._videoAutoplayObserver) {
            section._videoAutoplayObserver.disconnect();
            section._videoAutoplayObserver = null;
          }
          if (onScroll) {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            onScroll = null;
          }
          section.classList.remove("is-reveal-armed");
          section.dataset.ready = "false";
        };

        section._videoStoryCleanup = cleanup;

        const revealSection = () => {
          if (revealed) return;
          revealed = true;
          section.classList.add("is-visible");
          if (safetyTimer) {
            window.clearTimeout(safetyTimer);
            safetyTimer = null;
          }
        };

        if (!animateEnabled || reduceMotion || !("IntersectionObserver" in window)) {
          revealSection();
          setFinalCounts();
          return;
        }

        // Arm hide-until-reveal only after JS is ready (avoids blank section if script fails).
        section.classList.add("is-reveal-armed");

        // Never leave content hidden if the observer fails to fire.
        safetyTimer = window.setTimeout(() => {
          revealSection();
          setFinalCounts();
        }, 2500);

        const reveal = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              revealSection();
              reveal.disconnect();
            });
          },
          { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
        );
        reveal.observe(section);
        observers.push(reveal);

        requestAnimationFrame(() => {
          if (isInView(section)) {
            revealSection();
            reveal.disconnect();
          }
        });

        let counted = false;
        const countObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting || counted) return;
              counted = true;
              counters.forEach((item, index) => {
                const id = window.setTimeout(() => animateCount(item), index * 160);
                timers.push(id);
              });
              countObserver.disconnect();
            });
          },
          { threshold: 0.35 }
        );
        if (stats && counters.length > 0) {
          countObserver.observe(stats);
          observers.push(countObserver);
        } else {
          setFinalCounts();
        }

        let ticking = false;
        const updateParallax = () => {
          if (!section.classList.contains("is-visible")) return;
          const rect = section.getBoundingClientRect();
          const view = window.innerHeight || 1;
          if (rect.bottom < 0 || rect.top > view) return;
          const offset = (rect.top + rect.height / 2 - view / 2) / view;
          section.querySelectorAll("[data-parallax]").forEach((el) => {
            const speed = Number.parseFloat(el.dataset.parallax) || 0;
            el.style.transform = "translate3d(0, " + (offset * speed * 160).toFixed(2) + "px, 0)";
          });
        };

        onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            updateParallax();
            ticking = false;
          });
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll, { passive: true });
        updateParallax();
      } catch (_error) {
        if (typeof section._videoStoryCleanup === "function") {
          section._videoStoryCleanup();
        }
        if (section._videoAutoplayObserver) {
          section._videoAutoplayObserver.disconnect();
          section._videoAutoplayObserver = null;
        }
        section.classList.add("is-visible");
        section.querySelectorAll("[data-count-value]").forEach((wrap) => {
          const run = wrap.querySelector(".video-story__stat-run");
          if (run && wrap.dataset.countValue) {
            run.textContent = wrap.dataset.countValue;
          }
        });
        section.dataset.ready = "false";
      }
    });
  }

  const start = () => setup(document);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  document.addEventListener("shopify:section:load", (event) => setup(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target;
    if (!root || typeof root.querySelectorAll !== "function") return;
    const sections = root.matches?.("[data-video-story]")
      ? [root]
      : [...root.querySelectorAll("[data-video-story]")];
    sections.forEach((section) => {
      if (typeof section._videoStoryCleanup === "function") section._videoStoryCleanup();
    });
  });
})();
